import { AvatarWithBubbleIcon } from '~/components/basehub-avatar'
import { Notification } from '@basehub/data/replicache/index'
import * as React from 'react'
import { useTimeDiff } from '~/hooks/use-time-diff'
import { PrimitiveFeedbackCommentEditor } from '../../(repo)/[repo-slug]/(editor)/comments/components/editor'
import { safeParseCommentValue } from '../../(repo)/[repo-slug]/(editor)/comments/components'
import { getFeedbackExtensions } from '../../(repo)/[repo-slug]/(editor)/comments/components/editor/extensions'
import { getEventIcon } from './utils'
import { Cross1Icon } from '@radix-ui/react-icons'
import { ButtonLink } from '~/sotano-kit/primitives/button'
import { WorkflowNotificationContent } from './workflow-action'

interface NotificationItemProps {
  notification: Notification
  currentUserId: string
  onRead?: () => void
}

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  currentUserId,
  onRead,
}) => {
  const content = renderNotificationContent(notification)
  return (
    <NotificationItemWrapper
      isRead={notification.read}
      authorId={notification.authorId}
      authorUsername={notification.authorUsername}
      bubbleIcon={getEventIcon(notification)}
      label={
        <NotificationText
          notification={notification}
          currentUserId={currentUserId}
        />
      }
      onRead={onRead}
      createdAt={notification.createdAt}
      href={notification.href}
      branchName={notification.branchName}
      repoName={notification.repoName}
    >
      {content}
    </NotificationItemWrapper>
  )
}

const renderNotificationContent = (
  notification: Notification
): React.ReactNode => {
  switch (notification.type) {
    case 'repo-cr-resolution':
      return null
    case 'repo-thread-comment':
      return (
        <PrimitiveFeedbackCommentEditor
          content={safeParseCommentValue(notification.content)}
          editable={false}
          extensions={getFeedbackExtensions()}
          className="pl-2 border-l-2 border-dark-border-solid [&_.ProseMirror]:!cursor-default [&_.ProseMirror]:!z-0"
          immediatelyRender={false}
        />
      )
    case 'workflow-action':
      return <WorkflowNotificationContent notification={notification} />
    default:
      return null
  }
}

// =============================================================================
// Wrapper
// =============================================================================

interface NotificationItemWrapperProps {
  authorId: string
  authorUsername: string
  children?: React.ReactNode
  bubbleIcon: React.ReactNode
  label: React.ReactNode
  isRead: boolean
  createdAt: string
  onRead?: () => void
  href: string
  repoName?: string
  branchName: string
}

const NotificationItemWrapper: React.FC<NotificationItemWrapperProps> = ({
  authorId,
  authorUsername,
  children,
  bubbleIcon,
  label,
  isRead,
  createdAt,
  href,
  repoName,
  branchName,
  onRead,
}) => {
  const timeDiff = useTimeDiff(new Date(createdAt))
  const [, teamSlug, repoSlug] = href ? href.split('/') : ''
  const repo = {
    label: repoName ?? 'repo',
    href: `/${teamSlug}/${repoSlug}/explore`,
  }

  const branch = {
    label: branchName,
    href: `/${teamSlug}/${repoSlug}/explore/${branchName}/timeline`,
  }
  return (
    <div className="grid grid-cols-[auto,1fr] group gap-2 py-3 px-4 items-start relative overflow-hidden hover:bg-dark-shade max-w-full ring-0 hover:border-transparent hover:outline-none notification-item">
      {href && (
        <ButtonLink
          unstyled
          onClick={onRead}
          href={href}
          className="absolute top-0 left-0 w-full h-full z-10 outline-none hover:outline-none !cursor-default"
        />
      )}
      {!isRead && <UnreadIndicator markAsRead={onRead} />}
      <AvatarWithBubbleIcon
        id={authorId}
        avatarClassName="border-dark-border-solid"
        name={authorUsername}
        bubbleIcon={bubbleIcon}
        bubbleClassName="bg-dark-shade border-dark-border-solid"
      />
      <NotificationContent
        label={label}
        timeDiff={timeDiff}
        repo={repo}
        branch={branch}
        isRead={isRead}
      >
        {children}
      </NotificationContent>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Other Components
// -----------------------------------------------------------------------------

const UnreadIndicator = ({ markAsRead }: { markAsRead?: () => void }) => {
  return (
    <>
      <div className="flex items-center justify-center size-2 bg-dark-control-base rounded-full absolute top-6 right-3 opacity-100 group-hover:opacity-0" />
      <button
        className="flex z-20 items-center rounded-full justify-center absolute top-[18px] right-1.5 p-1 group-hover:opacity-100 opacity-0 text-dark-faint hover:bg-dark-shade-hover hover:text-dark-bright"
        onClick={markAsRead}
      >
        <Cross1Icon className="size-3 cursor-pointer" />
      </button>
    </>
  )
}

interface NotificationContentProps {
  label: React.ReactNode
  children?: React.ReactNode
  timeDiff: string
  isRead: boolean
  onRead?: () => void
  repo: {
    label: string
    href: string
  }
  branch: {
    label: string
    href: string
  }
}

const NotificationContent: React.FC<NotificationContentProps> = ({
  label,
  children,
  timeDiff,
  repo,
  branch,
}) => (
  <div className="flex flex-col w-full min-w-0 overflow-hidden">
    <div className="flex flex-col mb-1 pr-2.5">
      <p className="text-sm [&_a]:z-20">{label}</p>
      {children && <div className="py-1 w-full [&_a]:z-20">{children}</div>}
    </div>
    <div className="flex items-center text-xs text-dark-faint">
      <div className="flex-1 min-w-0 overflow-hidden flex">
        <ButtonLink
          unstyled
          className="hover:text-dark-bright relative z-20 truncate inline-block shrink-0 flex-1 max-w-[max-content] font-medium !-outline-offset-1"
          href={repo.href}
          title={repo.label}
        >
          {repo.label}
        </ButtonLink>{' '}
        <span className="px-1 inline-block leading-[1.35] font-extrabold text-sm">
          ·
        </span>{' '}
        <ButtonLink
          unstyled
          className="hover:text-dark-bright relative z-20 truncate inline-block flex-1 shrink-0 max-w-[max-content] font-medium !-outline-offset-1"
          href={branch.href}
          title={branch.label}
        >
          {branch.label}
        </ButtonLink>
      </div>
      <span className="text-dark-faint font-light whitespace-nowrap ml-2  shrink-0">
        {timeDiff}
      </span>
    </div>
  </div>
)

interface NotificationTextProps {
  notification: Notification
  currentUserId: string
}

// =============================================================================
// Notification Text
// =============================================================================

const NotificationText: React.FC<NotificationTextProps> = ({
  notification,
  currentUserId,
}) => {
  switch (notification.type) {
    case 'repo-cr-resolution':
      return <ResolutionNotification notification={notification} />
    case 'repo-thread-comment':
      return (
        <CommentNotification
          notification={notification}
          currentUserId={currentUserId}
        />
      )
    default:
      return null
  }
}

const ResolutionNotification: React.FC<{
  notification: Notification & { type: 'repo-cr-resolution' }
}> = ({ notification }) => {
  const teamSlug = notification.href.split('/')[1]
  const repoSlug = notification.href.split('/')[2]
  return (
    <span className="text-dark-dim text-sm">
      <ButtonLink
        unstyled
        href={`/${notification.authorUsername}`}
        className="text-dark-bright font-medium hover:underline relative z-20 !-outline-offset-1"
      >
        {notification.authorUsername}{' '}
      </ButtonLink>
      {notification.resolution === 'merged' ? (
        <>
          Merged{' '}
          <ButtonLink
            unstyled
            className="text-dark-bright font-medium hover:underline relative z-20 !-outline-offset-1"
            href={`/${teamSlug}/${repoSlug}/explore/${notification.baseBranchName}`}
          >
            {notification.baseBranchName}{' '}
          </ButtonLink>
          into{' '}
          <ButtonLink
            unstyled
            className="text-dark-bright font-medium hover:underline relative z-20 !-outline-offset-1"
            href={`/${teamSlug}/${repoSlug}/explore/${notification.branchName}`}
          >
            {notification.branchName}
          </ButtonLink>
        </>
      ) : notification.resolution === 'archived' ? (
        <>
          Archived{' '}
          <ButtonLink
            unstyled
            className="text-dark-bright font-medium hover:underline relative z-20 !-outline-offset-1"
            href={`/${teamSlug}/${repoSlug}/explore/${notification.branchName}`}
          >
            {notification.branchName}
          </ButtonLink>
        </>
      ) : null}
    </span>
  )
}

const CommentNotification: React.FC<{
  notification: Notification & { type: 'repo-thread-comment' }
  currentUserId: string
}> = ({ notification }) => {
  return (
    <span className="text-dark-dim text-sm">
      <ButtonLink
        unstyled
        href={`/${notification.authorUsername}`}
        className="text-dark-bright font-medium hover:underline relative z-20 !-outline-offset-1"
      >
        {notification.authorUsername}
      </ButtonLink>{' '}
      left a comment
    </span>
  )
}
